import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { User, Lock, Check, Sunrise, Camera, Flame } from "lucide-react";

const PRAYER_TIMES = [
    { v: "05:00", l: "5:00 AM — Dawn" },
    { v: "06:00", l: "6:00 AM — Morning" },
    { v: "12:00", l: "12:00 PM — Midday Angelus" },
    { v: "15:00", l: "3:00 PM — Divine Mercy Hour" },
    { v: "18:00", l: "6:00 PM — Evening Angelus" },
    { v: "21:00", l: "9:00 PM — Night" },
];

export default function Profile() {
    const { t } = useTranslation();
    const { user, refreshMe } = useAuth();

    const [name, setName] = useState(user?.name || "");
    const [prayerTime, setPrayerTime] = useState(user?.prayer_time || "");
    const [bio, setBio] = useState(user?.bio || "");
    const [savedAt, setSavedAt] = useState(0);
    const [profileErr, setProfileErr] = useState("");

    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [pwOk, setPwOk] = useState(false);
    const [pwErr, setPwErr] = useState("");

    const fileInput = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [photoErr, setPhotoErr] = useState("");

    const saveProfile = async (e) => {
        e.preventDefault();
        setProfileErr("");
        try {
            await api.put("/auth/me", { name, prayer_time: prayerTime, bio });
            await refreshMe();
            setSavedAt(Date.now());
        } catch (err) {
            setProfileErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const changePw = async (e) => {
        e.preventDefault();
        setPwErr(""); setPwOk(false);
        try {
            await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
            setPwOk(true);
            setCurrentPw(""); setNewPw("");
        } catch (err) {
            setPwErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPhotoErr("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post("/auth/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await refreshMe();
        } catch (err) {
            setPhotoErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (fileInput.current) fileInput.current.value = "";
        }
    };

    if (!user?.id) return null;

    const initial = (user.name || "U").trim().charAt(0).toUpperCase();

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-4xl px-6 md:px-10 py-12">
                <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>{t("profile.kicker")}</div>
                <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>{t("profile.h")}</h1>
                <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>{t("profile.sub")}</p>

                <div className="mt-10 grid lg:grid-cols-2 gap-6">
                    <form onSubmit={saveProfile} className="p-card p-6 sm:p-8" data-testid="profile-form">
                        <div className="flex items-center gap-2 mb-5">
                            <User size={16} style={{ color: "#7F1D1D" }} />
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>{t("profile.h")}</div>
                        </div>

                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("profile.name")}</label>
                        <input value={name} onChange={(e) => setName(e.target.value)}
                               className="w-full px-4 py-3 rounded-full border mb-4"
                               style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="profile-name-input" />

                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("profile.email")}</label>
                        <input value={user.email} disabled
                               className="w-full px-4 py-3 rounded-full border mb-4 opacity-70"
                               style={{ borderColor: "#E5E0D8", background: "#F5F0E6" }}
                               data-testid="profile-email-input" />

                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("profile.prayer_time")}</label>
                        <div className="grid grid-cols-2 gap-2 mb-5">
                            {PRAYER_TIMES.map((p) => (
                                <button type="button" key={p.v}
                                        onClick={() => setPrayerTime(p.v)}
                                        className="p-card p-2.5 text-left flex items-center gap-2 text-sm"
                                        style={{ borderColor: prayerTime === p.v ? "#D4AF37" : "#E5E0D8" }}
                                        data-testid={`profile-time-${p.v}`}>
                                    <Sunrise size={14} style={{ color: prayerTime === p.v ? "#D4AF37" : "#7F1D1D" }} />
                                    <span style={{ color: "#1E1B4B" }}>{p.l}</span>
                                </button>
                            ))}
                        </div>

                        <div className="text-sm mb-5" style={{ color: "#525252" }}>
                            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>{t("profile.is_warrior")}: </span>
                            <span style={{ color: "#1E1B4B" }}>{user.is_warrior ? t("profile.warrior_yes") : t("profile.warrior_no")}</span>
                        </div>

                        {user.is_warrior && (
                            <div className="mt-6 pt-5 border-t" style={{ borderColor: "#E5E0D8" }} data-testid="profile-warrior-bio">
                                <div className="flex items-center gap-2 mb-1">
                                    <Flame size={14} style={{ color: "#D4AF37" }} />
                                    <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>{t("profile.warrior_bio_h")}</div>
                                </div>
                                <div className="text-xs mb-3" style={{ color: "#525252" }}>{t("profile.warrior_bio_sub")}</div>

                                <div className="flex items-center gap-4 mb-4">
                                    <div className="relative">
                                        <div className="h-20 w-20 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                                             style={{ background: "#1E1B4B", color: "#D4AF37", border: "2px solid #D4AF37" }}>
                                            {user.photo_url ? (
                                                <img src={user.photo_url} alt={user.name} className="h-full w-full object-cover" data-testid="profile-photo-preview" />
                                            ) : (
                                                <span className="font-display text-3xl">{initial}</span>
                                            )}
                                        </div>
                                        {uploading && (
                                            <div className="absolute inset-0 rounded-full flex items-center justify-center" style={{ background: "rgba(30,27,75,0.6)", color: "#FDFBF7", fontSize: 11 }}>
                                                {t("profile.uploading")}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="profile-photo-file" />
                                        <button type="button" onClick={() => fileInput.current?.click()} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="profile-photo-button">
                                            <Camera size={14} /> {user.photo_url ? t("profile.change_photo") : t("profile.upload_photo")}
                                        </button>
                                        <div className="text-xs mt-1.5" style={{ color: "#525252" }}>{t("profile.photo_hint")}</div>
                                        {photoErr && <div className="text-xs mt-1" style={{ color: "#7F1D1D" }} data-testid="profile-photo-error">{photoErr}</div>}
                                    </div>
                                </div>

                                <textarea rows={4} maxLength={500} value={bio}
                                          onChange={(e) => setBio(e.target.value)}
                                          placeholder={t("profile.bio")}
                                          className="w-full px-4 py-3 rounded-2xl border"
                                          style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                          data-testid="profile-bio-input" />
                                <div className="text-[11px] mt-1 text-right" style={{ color: "#525252" }}>{bio.length}/500</div>
                            </div>
                        )}

                        {profileErr && <div className="mb-3 text-sm" style={{ color: "#7F1D1D" }} data-testid="profile-error">{profileErr}</div>}
                        <button type="submit" className="btn-primary" data-testid="profile-save-button">{t("profile.save")}</button>
                        {savedAt > 0 && (
                            <span className="ml-3 text-sm inline-flex items-center gap-1" style={{ color: "#7F1D1D" }} data-testid="profile-saved">
                                <Check size={14} /> {t("profile.saved")}
                            </span>
                        )}
                    </form>

                    <form onSubmit={changePw} className="p-card p-6 sm:p-8" data-testid="password-form">
                        <div className="flex items-center gap-2 mb-5">
                            <Lock size={16} style={{ color: "#7F1D1D" }} />
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>{t("profile.pw_h")}</div>
                        </div>
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("profile.pw_current")}</label>
                        <input type="password" required value={currentPw}
                               onChange={(e) => setCurrentPw(e.target.value)}
                               className="w-full px-4 py-3 rounded-full border mb-4"
                               style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="password-current-input" />
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("profile.pw_new")}</label>
                        <input type="password" required minLength={6} value={newPw}
                               onChange={(e) => setNewPw(e.target.value)}
                               className="w-full px-4 py-3 rounded-full border mb-4"
                               style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="password-new-input" />
                        {pwErr && <div className="mb-3 text-sm" style={{ color: "#7F1D1D" }} data-testid="password-error">{pwErr}</div>}
                        <button type="submit" className="btn-primary" data-testid="password-submit-button">{t("profile.pw_submit")}</button>
                        {pwOk && (
                            <span className="ml-3 text-sm inline-flex items-center gap-1" style={{ color: "#7F1D1D" }} data-testid="password-ok">
                                <Check size={14} /> {t("profile.pw_ok")}
                            </span>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}
